// @flow
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CreationTaskDrawer } from 'features/daily-plan';
import { TASK_TYPES } from 'features/daily-plan/constants';
import { useProfile } from 'hooks/profileActions';
import { useSearchProjectInProfiles } from 'hooks/searchProject';
import { EntityRoutes } from 'routes/constants';
import { Permissions, userHasPermission } from 'services/Authorization';
import { ContactIncludes } from 'services/includes';
import strings from 'strings';
import { When } from 'UI/components/atoms/When';
import {
  getOptOutDate,
  handleEntityEditionCompleted
} from 'UI/components/organisms/inventoryProfiles/utils';
import LinkCompanyDialog from 'UI/components/organisms/LinkCompanyDialog';
import NameEdit from 'UI/components/organisms/NameEdit';
import InventoryProfileLayout from 'UI/components/templates/InventoryProfileLayout';
import { DateFormats } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import {
  ContactEntity,
  EntityType,
  SearchProjectEntities,
  TabKeys
} from 'UI/constants/entityTypes';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';
import { getId, localTimeFormatter } from 'UI/utils';

import { useContactProfileFeatureFlags } from '../ContactProfile.hooks';
import { ALLOWED_TASKS_BY_CONTACT_ROLE } from '../tabs';

type ContactProfileProps = {
  id: number,
  enableUrlNavigation: boolean
};

const createTabsProps = ({ contactId, fullName, searchProjectTabPropsDefinition }) => [
  {
    id: TabKeys.ActivityLogs,
    tabProps: {
      itemName: fullName,
      profileId: contactId,
      profileModule: EntityType.Contact
    }
  },
  searchProjectTabPropsDefinition,
  {
    id: TabKeys.Notes,
    tabProps: {
      itemName: fullName,
      profileId: contactId,
      profileModule: EntityType.Contact
    }
  },
  {
    id: TabKeys.Tasks,
    tabProps: {
      itemName: fullName,
      profileId: contactId
    }
  },
  {
    id: TabKeys.DataSheet,
    tabProps: {
      contactId
    }
  }
];

const ContactProfileContent = ({
  id,
  layoutClassName,
  enableUrlNavigation,
  onEditionCompleted
}: ContactProfileProps) => {
  const history = useHistory();
  const endpoint = `${Endpoints.Names}/${id}`;

  const {
    canOrganizeTabs,
    isAddDailyPlanTaskEnabled,
    isChangeLogsTabEnabled,
    isEnabledAddAsACandidate,
    shouldShowKeywords
  } = useContactProfileFeatureFlags();

  const [tabKeys, setTabKeys] = useState({
    [TabKeys.SearchProjects]: 0
  });

  const canUseDailyPlanTask = userHasPermission(Permissions.DailyPlan.CanUseModule);

  const triggerTabRefresh = tabName => setTabKeys(prev => ({ ...prev, [tabName]: getId() }));

  const {
    handleEditClick,
    handleEditClosed,
    handleProfileStateUpdate,
    isLoading,
    refreshData,
    results,
    state,
    status,
    uiState,
    handleProfileUiState,
    setUiState
  } = useProfile({
    entity: EntityType.Contact,
    profileId: id,
    apiVersion: 2,
    includes: `${ContactIncludes}${shouldShowKeywords ? ',keywordTags' : ''}`,
    shouldLoadTitle: enableUrlNavigation
  });

  const fullName = results?.personalInformation?.full_name;
  const lastActivity = results?.last_activity_date;

  const formattedDate = localTimeFormatter(lastActivity, DateFormats.SimpleDateTime);

  const handleCompanyUpdate = async ({ current_company, company_id }) => {
    handleProfileStateUpdate(prev => ({
      ...prev,
      current_company,
      company_id
    }));
    await refreshData();
  };

  const handleEditCompleted = async () => {
    handleProfileUiState({ statesToHandle: { isEditOpen: false } });
    await refreshData(false);
  };

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   S E A R C H   P R O J E C T S   S E T T I N G S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const { SearchProjectForms, SearchProjectsMenuItems, searchProjectTabPropsDefinition } =
    useSearchProjectInProfiles({
      endpoint,
      entity: SearchProjectEntities.Contact,
      profile: {
        id: results?.id,
        name: fullName
      },
      tabKey: tabKeys.searchProjects,
      triggerTabRefresh,
      shouldRedirectToSearchProjectPreview: enableUrlNavigation
    });

  const tabsProps = createTabsProps({
    contactId: id,
    fullName,
    searchProjectTabPropsDefinition
  });

  const headerProps = {
    sectionName: 'Contacts',
    title: fullName
  };

  const handleCreateCandidateClick = () =>
    history.push(`${EntityRoutes.CandidateCreate}?contactId=${id}`);

  const customMenuItems = [
    ...SearchProjectsMenuItems,
    {
      title: strings.inventoryProfiles.common.candidateAddition,
      action: () => handleCreateCandidateClick(),
      visible: isEnabledAddAsACandidate
    },
    {
      title: 'Add Action',
      action: () => setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: true })),
      visible: isAddDailyPlanTaskEnabled && canUseDailyPlanTask
    }
  ];

  const { optOutDate } = getOptOutDate(results);

  const summaryLabels = [
    { title: strings.inventoryProfiles.common.optOut.creation, content: optOutDate }
  ];
  const handleTaskDrawerClose = () => {
    setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: false }));
  };

  const handleOnTaskCreated = () => {
    handleTaskDrawerClose();
    history.push(EntityRoutes.DailyPlan);
  };

  return (
    <>
      <InventoryProfileLayout
        activityLabel={formattedDate}
        addItemLabel="Add a Collaborator"
        contentPageLayoutProps={{ className: layoutClassName }}
        enableUrlNavigation={enableUrlNavigation}
        entityType={ContactEntity}
        headerCardProps={headerProps}
        inventoryType={results?.hiringAuthorityStatus}
        isChangeLogsTabEnabled={isChangeLogsTabEnabled}
        results={results}
        summaryLabels={summaryLabels}
        profileMenuProps={{
          customMenuItems,
          onEditClick: handleEditClick
        }}
        ProfileTabsProps={{
          endpoint,
          organizationStorageKey: canOrganizeTabs ? PERSISTENCE_KEYS.PROFILE_TABS.CONTACTS : null,
          organizationSubheader: `${ContactEntity.singular} ${strings.inventoryProfiles.title}`,
          profileTabProps: tabsProps
        }}
        ProfileUiStateManagerProps={{
          entity: ContactEntity,
          entityRoute: EntityRoutes.Contacts,
          onRefreshProfileClick: refreshData,
          responseStatusCode: state.responseStatusCode,
          status
        }}
      />
      <When condition={!isLoading}>
        <SearchProjectForms />
        <When condition={results?.shouldAskRecruiterAddEmployer}>
          <LinkCompanyDialog
            type={EntityType.ContactType.Contact}
            entity={results}
            onSetProfile={handleCompanyUpdate}
          />
        </When>
        <When condition={uiState.isEditOpen}>
          <NameEdit
            FPDrawerProps={{ open: uiState.isEditOpen }}
            name={results}
            onEditCompleted={handleEntityEditionCompleted({
              handleEditCompleted,
              enableUrlNavigation,
              onEditionCompleted
            })}
            onEditClosed={handleEditClosed}
          />
        </When>
      </When>

      <When condition={uiState.isDailyPlanTaskDrawerOpen}>
        <CreationTaskDrawer
          allowedTaskTypes={
            ALLOWED_TASKS_BY_CONTACT_ROLE[results?.status?.id] ?? [TASK_TYPES.other]
          }
          onClose={handleTaskDrawerClose}
          entityData={{
            id: results.id,
            name: results?.personalInformation?.full_name
          }}
          entityType={EntityType.Contact}
          onTaskCreated={handleOnTaskCreated}
        />
      </When>
    </>
  );
};

export default ContactProfileContent;
